import {PayloadAction, createSelector, createSlice} from '@reduxjs/toolkit';
import last from 'lodash/last';

import {THasActiveFlowError, TTemplateMessageError, endpoints} from './api';
import {TCyclePair, TCycles, TErrors, TFlowStep} from './types';
import {getCyclePairs} from './utils';

type TState = {
  filters: TAnyObject;
  flowStepErrors: TErrors;
  flowCycles: TCyclePair[][];
};

const initialState: TState = {
  filters: {},
  flowStepErrors: {},
  flowCycles: [],
};

const triggersSlice = createSlice({
  name: 'triggers',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<TAnyObject>) {
      state.filters = action.payload;
    },
    setFlowStepErrors(state, action: PayloadAction<TErrors>) {
      state.flowStepErrors = action.payload;
    },
    setBackendFlowTemplateErrors(state, action: PayloadAction<TTemplateMessageError>) {
      state.flowStepErrors = action.payload.args.paths.reduce(
        (acc, path) => ({...acc, [last(path) as string]: [{code: action.payload.title}]}),
        {},
      );
    },
    setBackendHasFlowErrors(
      state,
      action: PayloadAction<{step: TFlowStep; error: THasActiveFlowError}>,
    ) {
      state.flowStepErrors[action.payload.step.id] = [
        {code: action.payload.error.title, args: action.payload.error.args},
      ];
    },
    setFlowCycles(state, action: PayloadAction<TCycles>) {
      state.flowCycles = action.payload.map(getCyclePairs);
    },
    removeFlowCycle(state, action: PayloadAction<TCyclePair>) {
      state.flowCycles = state.flowCycles.filter(
        (pairs) =>
          !pairs.find(
            (pair) =>
              pair.source === action.payload.source && pair.target === action.payload.target,
          ),
      );
    },
    removeFlowStepError(state, action: PayloadAction<string>) {
      delete state.flowStepErrors[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(endpoints.activateFlow.matchFulfilled, (state) => {
      state.filters = {};
    });
    builder.addMatcher(endpoints.deactivateFlow.matchFulfilled, (state) => {
      state.filters = {};
    });
    builder.addMatcher(endpoints.deleteFlow.matchFulfilled, (state) => {
      state.filters = {};
    });
  },
});

export const {
  setFilters: setFiltersAction,
  setFlowStepErrors: setFlowStepErrorsAction,
  setBackendFlowTemplateErrors: setBackendFlowTemplateErrorsAction,
  setBackendHasFlowErrors: setBackendHasFlowErrorsAction,
  setFlowCycles: setFlowCyclesAction,
  removeFlowStepError: removeFlowStepErrorAction,
  removeFlowCycle: removeFlowCycleAction,
} = triggersSlice.actions;

export const selectFilters = (state: TRootState) => state.triggers.filters;
const triggersSelect = (state: TRootState) => state;

export const selectFlowCycles = (state: TRootState) => state.triggers.flowCycles;
export const selectFlowStepErrors = (state: TRootState) =>
  Object.values(state.triggers.flowStepErrors).flatMap((errors) => errors);

export const selectFlowStepErrorsById = (id: string) => {
  return createSelector(triggersSelect, (state) => state.triggers.flowStepErrors[id]);
};
export default triggersSlice.reducer;
